import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { listTenants } from '../../services/tenantService';
import { setImpersonatingTenantId } from '../../services/impersonationService';
import { IMPERSONATION_TTL_HOURS } from '../../constants/impersonation';
import {
  isPathAllowedForRole,
  getDefaultRouteForRole,
  getToolsSections,
  TOOLS_COLUMN_COUNT,
} from '../../constants/rolePermissions';
import { LifeBuoy, KeyRound, ShieldCheck } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import ForcePasswordChangeScreen from '../ChangePassword/ForcePasswordChangeScreen';
import ChangePasswordModal from '../ChangePassword/ChangePasswordModal';
import ConnectMicrosoftScreen from '../Security/ConnectMicrosoftScreen';
import ForceMfaEnrollmentScreen from '../Security/ForceMfaEnrollmentScreen';
import MfaSettingsModal from '../Security/MfaSettingsModal';
import MfaGraceBanner from '../Security/MfaGraceBanner';
import IdleWarningModal from '../Security/IdleWarningModal';
import AppBrandMark from '../AppBrandMark';
import { subscribeToOpenTicketCount, getTeamUidsForSupervisor } from '../../services/helpdeskService';
import { useIsMobileViewport } from '../../utils/platformDetect';
import MobileOnlyDesktopNotice from '../MobileOnlyDesktopNotice';
import HelpdeskTicketForm from '../HelpdeskTicketForm';
import { unlockAlertAudio } from '../../utils/alertAudio';
import useBonusAccess from '../../hooks/useBonusAccess';
import './DashboardLayout.css';

/* Routes that are not appropriate for the field-care-manager mobile workflow.
   Accessing them on mobile shows a "Desktop only" notice instead of the page.
   The Patients list, patient detail, the Dashboard, and Helpdesk remain available.

   Everything reachable only from the Tools dropdown belongs here, because Tools
   is hidden on mobile — otherwise a bookmark would land on an unusable page. */
const DESKTOP_ONLY_PAGES = [
  ['/reports', 'Reports'],
  ['/users', 'User management'],
  ['/patient-assignments', 'Patient assignments'],
  ['/bulk-messaging', 'Bulk messaging'],
  ['/pathways', 'Pathway builder'],
  ['/bulk-pathways', 'Bulk pathways'],
  ['/billing-setup', 'Billing setup'],
  ['/insurances', 'Insurances'],
  ['/admin', 'Admin'],
  ['/rpm/devices/assignments', 'Patient devices'],
  ['/rpm/devices/setup', 'Device setup'],
  ['/rpm/devices/war-room', 'Device war room'],
  ['/rpm/devices/help-queue', 'Pairing help queue'],
  ['/rpm/devices/reading-charts', 'Reading charts'],
  ['/rpm/devices/sn-backfilling', 'Missing serial numbers'],
  ['/alerts/reading-quarantine', 'Reading quarantine'],
  ['/superuser', 'Superuser tools'],
  ['/education-library', 'Education library'],
];

/* Superusers can open these from the mobile hamburger; other roles still
   get the Desktop-only notice. */
const SUPERUSER_MOBILE_ALLOWED_PREFIXES = [
  '/rpm/devices/setup',
  '/rpm/devices/war-room',
];

function pathMatchesPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

function pickDesktopOnlyPageMeta(pathname) {
  if (!pathname) return null;
  for (const [prefix, pageName] of DESKTOP_ONLY_PAGES) {
    if (pathMatchesPrefix(pathname, prefix)) {
      return { pageName };
    }
  }
  return null;
}

export default function DashboardLayout() {
  const {
    profile, signOut, isSuperuser, impersonatingTenantId, refreshProfile, permissions,
    securityGate, hasPasswordProvider, isMicrosoftUser,
  } = useAuth();
  const [mfaSettingsOpen, setMfaSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const isMobile = useIsMobileViewport();
  const desktopOnlyPageMeta = useMemo(() => {
    if (!isMobile) return null;
    const meta = pickDesktopOnlyPageMeta(location.pathname);
    if (!meta) return null;
    if (
      isSuperuser &&
      SUPERUSER_MOBILE_ALLOWED_PREFIXES.some((prefix) =>
        pathMatchesPrefix(location.pathname, prefix)
      )
    ) {
      return null;
    }
    return meta;
  }, [isMobile, location.pathname, isSuperuser]);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [superuserDropdownOpen, setSuperuserDropdownOpen] = useState(false);
  const [tenantList, setTenantList] = useState([]);
  const [impersonationBusy, setImpersonationBusy] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [helpFormOpen, setHelpFormOpen] = useState(false);
  const toolsDropdownRef = useRef(null);
  const bonusAccess = useBonusAccess();
  const superuserDropdownRef = useRef(null);

  /* One capability-driven "Tools" menu replaces the former RPM Devices,
     Admin Tools, Pathways, and top-level team links, so the header stops
     wrapping on laptop-width screens. */
  const toolsSections = useMemo(() => getToolsSections(permissions), [permissions]);
  const toolsColumns = useMemo(() => {
    const cols = Array.from({ length: TOOLS_COLUMN_COUNT }, () => []);
    toolsSections.forEach((section) => {
      const idx = Math.min(section.column ?? 0, TOOLS_COLUMN_COUNT - 1);
      cols[idx].push(section);
    });
    return cols.filter((c) => c.length > 0);
  }, [toolsSections]);
  const toolsPaths = useMemo(
    () => toolsSections.flatMap((s) => s.links.map((l) => l.to)),
    [toolsSections],
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Unlock the shared alert AudioContext on the first user interaction anywhere
  // in the app shell (browsers block audio until a gesture). Covers mouse, touch,
  // pen, and keyboard so alert sounds work as soon as the user interacts.
  useEffect(() => {
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const onGesture = () => unlockAlertAudio();
    events.forEach((e) => document.addEventListener(e, onGesture));
    return () => events.forEach((e) => document.removeEventListener(e, onGesture));
  }, []);

  // Only roles that surface the badge need the live open-ticket subscription.
  const showTicketBadge = permissions.canSeeHelpdeskTicketBadge === true;

  useEffect(() => {
    if (!profile?.uid || !showTicketBadge) {
      setOpenTicketCount(0);
      return undefined;
    }
    let cancelled = false;
    let unsub = null;
    (async () => {
      let teamUids = [];
      if (profile.role === 'supervisor') {
        teamUids = await getTeamUidsForSupervisor(profile.uid);
      }
      if (cancelled) return;
      unsub = subscribeToOpenTicketCount(
        { uid: profile.uid, role: profile.role, teamUids },
        (count) => { if (!cancelled) setOpenTicketCount(count); },
      );
      if (cancelled) unsub();
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [profile?.uid, profile?.role, showTicketBadge]);

  const isSuperuserOnlyBillingPath =
    location.pathname === '/billing-setup/service-types' ||
    location.pathname === '/billing-setup/reimbursement-methods';
  /* Highlight the Tools trigger for any page it can reach, including redirect
     aliases that resolve to a menu destination. */
  const isToolsPath = useMemo(() => {
    const path = location.pathname;
    if (isSuperuserOnlyBillingPath) return false;
    const aliases = {
      '/rpm/devices/discovery': '/rpm/devices/war-room',
      '/admin/help-phone': '/admin/phone-numbers',
    };
    const resolved = aliases[path] || path;
    return toolsPaths.some((p) => resolved === p || resolved.startsWith(p + '/'));
  }, [location.pathname, toolsPaths, isSuperuserOnlyBillingPath]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(e.target)) {
        setToolsDropdownOpen(false);
      }
      if (superuserDropdownRef.current && !superuserDropdownRef.current.contains(e.target)) {
        setSuperuserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isSuperuser) {
      setTenantList([]);
      return;
    }
    let cancelled = false;
    listTenants()
      .then((rows) => {
        if (!cancelled) setTenantList(rows.filter((t) => t.active !== false));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSuperuser]);

  const handleImpersonationChange = useCallback(
    async (e) => {
      const v = e.target.value;
      setImpersonationBusy(true);
      try {
        await setImpersonatingTenantId(v || null);
        await refreshProfile();
      } catch (err) {
        console.warn(err);
      } finally {
        setImpersonationBusy(false);
      }
    },
    [refreshProfile],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const closeTools = () => setToolsDropdownOpen(false);

  // Security gates (docs/PLAN_SECURITY_HARDENING_2026_10.md): Trilogy staff must
  // connect Microsoft, temp-password accounts must pick a password, and once the
  // grace window ends every session needs a proven second factor.
  if (securityGate === 'connect-microsoft') return <ConnectMicrosoftScreen />;
  if (securityGate === 'change-password') return <ForcePasswordChangeScreen />;
  if (securityGate === 'enroll-mfa') return <ForceMfaEnrollmentScreen />;

  // Central route guard for restricted roles (viewer, clinical admin):
  // anything outside the role's allowed pages redirects to its home page.
  if (profile && !isPathAllowedForRole(profile.role, location.pathname)) {
    return <Navigate to={getDefaultRouteForRole(profile.role)} replace />;
  }

  return (
    <div className="dashboard-layout">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <AppBrandMark />
        </div>
        <button
          type="button"
          className="dashboard-hamburger"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-expanded={mobileNavOpen}
          aria-label="Toggle navigation"
        >
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
        </button>
        <nav className={`dashboard-nav${mobileNavOpen ? ' dashboard-nav--open' : ''}`}>
          {permissions.nav.dashboard && (
            <>
              <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                RPM Dashboard
              </NavLink>
              {/* Mobile-only Dashboard sub-views (hidden on desktop) */}
              <NavLink
                to="/?view=alerts"
                className="nav-link nav-link--mobile-only"
                onClick={() => setMobileNavOpen(false)}
              >
                &nbsp;&nbsp;Dashboard: Alerts
              </NavLink>
              <NavLink
                to="/?view=messages"
                className="nav-link nav-link--mobile-only"
                onClick={() => setMobileNavOpen(false)}
              >
                &nbsp;&nbsp;Dashboard: Messages
              </NavLink>
            </>
          )}
          {permissions.nav.careQueue && (
            <NavLink to="/care-queue" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              Care Queue
            </NavLink>
          )}
          <NavLink to="/patients" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
            Patients
          </NavLink>
          {permissions.nav.worklist && (
            <NavLink to="/worklist" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              Worklist
            </NavLink>
          )}
          <NavLink to="/reports" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
            Reports
          </NavLink>
          {permissions.canSeeMyBonus && bonusAccess.hasScorecard && (
            <NavLink to="/my-bonus" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              My Bonus
            </NavLink>
          )}
          {permissions.canSeeAdminDashboard && (
            <NavLink to="/admin-dashboard" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              Admin
            </NavLink>
          )}
          {toolsSections.length > 0 ? (
            <div className="nav-dropdown-wrap" ref={toolsDropdownRef}>
              <button
                type="button"
                className={`nav-link nav-dropdown-trigger ${toolsDropdownOpen || isToolsPath ? 'active' : ''}`}
                onClick={() => setToolsDropdownOpen((v) => !v)}
                aria-expanded={toolsDropdownOpen}
                aria-haspopup="true"
              >
                Tools ▾
              </button>
              {toolsDropdownOpen && (
                <div className="nav-dropdown nav-dropdown--tools nav-mega-menu">
                  {toolsColumns.map((sections, colIdx) => (
                    <div className="nav-mega-col" key={sections[0]?.id || colIdx}>
                      {sections.map((section) => (
                        <div className="nav-dropdown-section" key={section.id}>
                          <div className="nav-dropdown-section-label">{section.label}</div>
                          {section.links.map((link) => (
                            <NavLink
                              to={link.to}
                              key={link.to}
                              className="nav-dropdown-link"
                              onClick={closeTools}
                            >
                              {link.label}
                              {link.badge === 'openTickets' && showTicketBadge && openTicketCount > 0 && (
                                <span className="nav-badge">{openTicketCount}</span>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {/* Tools is desktop-only, but Helpdesk is the field workflow for raising
              and tracking issues -- keep it directly reachable on mobile. */}
          {permissions.canSeeHelpdesk && (
            <NavLink
              to="/helpdesk"
              className="nav-link nav-link--mobile-only"
              onClick={() => setMobileNavOpen(false)}
            >
              Helpdesk
              {showTicketBadge && openTicketCount > 0 && <span className="nav-badge">{openTicketCount}</span>}
            </NavLink>
          )}
          {isSuperuser ? (
            <>
              <NavLink
                to="/rpm/devices/setup"
                className="nav-link nav-link--mobile-only"
                onClick={() => setMobileNavOpen(false)}
              >
                RPM Devices
              </NavLink>
              <NavLink
                to="/rpm/devices/war-room"
                className="nav-link nav-link--mobile-only"
                onClick={() => setMobileNavOpen(false)}
              >
                RPM War Room
              </NavLink>
              <div className="nav-dropdown-wrap" ref={superuserDropdownRef}>
                <button
                  type="button"
                  className={`nav-link nav-dropdown-trigger ${superuserDropdownOpen || location.pathname.startsWith('/superuser') || isSuperuserOnlyBillingPath ? 'active' : ''}`}
                  onClick={() => setSuperuserDropdownOpen((v) => !v)}
                  aria-expanded={superuserDropdownOpen}
                  aria-haspopup="true"
                >
                  Superuser ▾
                </button>
                {superuserDropdownOpen && (
                  <div className="nav-dropdown nav-dropdown--superuser-tools">
                    <div className="nav-dropdown-section">
                      <div className="nav-dropdown-section-label">Superuser only</div>
                      <NavLink
                        to="/superuser/tenants"
                        className="nav-dropdown-link"
                        onClick={() => setSuperuserDropdownOpen(false)}
                      >
                        Tenants
                      </NavLink>
                      <NavLink
                        to="/superuser/reset-consent"
                        className="nav-dropdown-link"
                        onClick={() => setSuperuserDropdownOpen(false)}
                      >
                        Reset Demo Consent
                      </NavLink>
                      <NavLink
                        to="/billing-setup/service-types"
                        className="nav-dropdown-link"
                        onClick={() => setSuperuserDropdownOpen(false)}
                      >
                        Service Types
                      </NavLink>
                      <NavLink
                        to="/billing-setup/reimbursement-methods"
                        className="nav-dropdown-link"
                        onClick={() => setSuperuserDropdownOpen(false)}
                      >
                        Reimbursement Methods
                      </NavLink>
                      <NavLink
                        to="/superuser/rate-schedules"
                        className="nav-dropdown-link"
                        onClick={() => setSuperuserDropdownOpen(false)}
                      >
                        Rate Schedules
                      </NavLink>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </nav>
        <div className="dashboard-user">
          {isSuperuser ? (
            <label className="dashboard-impersonation" htmlFor="dashboard-impersonate-tenant">
              <span className="dashboard-impersonation-label">Act as tenant</span>
              <select
                id="dashboard-impersonate-tenant"
                name="impersonatingTenantId"
                value={impersonatingTenantId || ''}
                onChange={handleImpersonationChange}
                disabled={impersonationBusy}
                aria-label="Impersonate tenant"
              >
                <option value="">— All tenants (global) —</option>
                {tenantList.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.shortName || t.id}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className="btn-help"
            title="Get help / submit a ticket"
            aria-label="Get help or submit a support ticket"
            onClick={() => setHelpFormOpen(true)}
          >
            <LifeBuoy size={18} />
          </button>
          {hasPasswordProvider && !isMicrosoftUser && (
            <button
              type="button"
              className="btn-help"
              title="Change my password"
              aria-label="Change my password"
              onClick={() => setChangePasswordOpen(true)}
            >
              <KeyRound size={18} />
            </button>
          )}
          <button
            type="button"
            className="btn-help"
            title="Two-step verification"
            aria-label="Two-step verification settings"
            onClick={() => setMfaSettingsOpen(true)}
          >
            <ShieldCheck size={18} />
          </button>
          <ThemeToggle />
          <span className="user-name">{profile?.displayName || profile?.email || 'User'}</span>
          <span className="user-role">({profile?.role || '—'})</span>
          <button type="button" onClick={handleSignOut} className="btn-signout">
            Sign out
          </button>
        </div>
      </header>
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      <MfaSettingsModal open={mfaSettingsOpen} onClose={() => setMfaSettingsOpen(false)} />
      <IdleWarningModal />
      <MfaGraceBanner onSetUp={() => setMfaSettingsOpen(true)} />
      {helpFormOpen && (
        <HelpdeskTicketForm
          open={helpFormOpen}
          onClose={() => setHelpFormOpen(false)}
          onCreated={() => setHelpFormOpen(false)}
        />
      )}
      {isSuperuser && impersonatingTenantId ? (
        <div className="dashboard-impersonation-banner" role="status">
          Viewing data as tenant <strong>{impersonatingTenantId}</strong> (impersonation). Billing and lists are scoped to
          this tenant. Session clears automatically after {IMPERSONATION_TTL_HOURS} hours from the last time impersonation
          was started.
        </div>
      ) : null}
      <main className="dashboard-main">
        {desktopOnlyPageMeta ? (
          <MobileOnlyDesktopNotice pageName={desktopOnlyPageMeta.pageName} />
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
